import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  Save, Globe, Loader2, Plus, X, 
  Check, Star, Video, 
  LayoutTemplate, Download, Zap
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { format } from "date-fns";

const defaultPlan = {
  name: "",
  price: 0,
  features: [],
  shopify_link: "",
  shopify_variant_id: "",
  is_popular: false
};

const defaultProposal = {
  hotel_name: "",
  location: "",
  website_url: "",
  contact_person: "",
  email: "",
  phone: "",
  hero_images: [],
  scraped_images: [], // Track which images were fetched
  intro_message_en: "",
  intro_message_de: "",
  platform_vision_en: "",
  platform_vision_de: "",
  plan_a: { ...defaultPlan, name: "Starter" },
  plan_b: { ...defaultPlan, name: "Professional", is_popular: true },
  plan_c: { ...defaultPlan, name: "Business" },
  plan_d: { ...defaultPlan, name: "Enterprise" },
  offer_start: null,
  offer_end: null,
  discount_percent: 0,
  discount_code: "",
  page_password: "",
  access_type: "public",
  cta_text_en: "Book Now",
  cta_text_de: "Jetzt Buchen",
  notes_en: "",
  notes_de: "",
  status: "draft",
  add_on_ids: [],
  benefit_ids: [],
  deal_id: null,
  company_id: null
};

export default function ProposalEditor() {
  const { id } = useParams();
  const isEditing = !!id;
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [proposal, setProposal] = useState(defaultProposal);
  const [addOns, setAddOns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [newFeature, setNewFeature] = useState("");
  const [allFeatures, setAllFeatures] = useState([]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  // Collect all unique features from all plans
  useEffect(() => {
    const features = new Set();
    ['plan_a', 'plan_b', 'plan_c', 'plan_d'].forEach(key => {
      (proposal[key]?.features || []).forEach(f => features.add(f));
    });
    setAllFeatures(Array.from(features));
  }, [proposal.plan_a?.features, proposal.plan_b?.features, proposal.plan_c?.features, proposal.plan_d?.features]);

  const fetchData = async () => {
    try {
      const [addOnsRes, templatesRes, dealsRes] = await Promise.all([
        axios.get(`${API}/addons`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/templates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/deals`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAddOns(addOnsRes.data);
      setTemplates(templatesRes.data);
      setDeals(dealsRes.data);

      if (isEditing) {
        const res = await axios.get(`${API}/proposals/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProposal({...defaultProposal, ...res.data});
      } else {
        // Apply default template if one exists
        const defaultTemplate = templatesRes.data.find(t => t.is_default);
        if (defaultTemplate) {
          applyTemplate(defaultTemplate);
        }
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template) => {
    const templateSections = template.sections;
    if (templateSections && Object.keys(templateSections).length > 0) {
      // New section-based template — stamp sections
      setProposal(prev => ({
        ...prev,
        sections: JSON.parse(JSON.stringify(templateSections)),
        add_on_ids: template.default_add_on_ids || prev.add_on_ids,
      }));
    } else {
      // Legacy template fallback (plan_a/plan_b/plan_c/plan_d)
      setProposal(prev => ({
        ...prev,
        plan_a: template.plan_a || prev.plan_a,
        plan_b: template.plan_b || prev.plan_b,
        plan_c: template.plan_c || prev.plan_c,
        plan_d: template.plan_d || prev.plan_d,
        add_on_ids: template.default_add_on_ids || prev.add_on_ids,
        feature_definitions: template.feature_definitions || prev.feature_definitions || []
      }));
    }
    setShowTemplateSelector(false);
    toast.success(`Template "${template.name}" applied`);
  };

  const handleScrapeWebsite = async () => {
    if (!proposal.website_url) {
      toast.error("Please enter a website URL first");
      return;
    }

    setScraping(true);
    try {
      const res = await axios.post(`${API}/scrape-hotel`, 
        { url: proposal.website_url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = res.data;
      const scrapedImages = data.images.slice(0, 8);
      setProposal(prev => ({
        ...prev,
        hotel_name: prev.hotel_name || data.title,
        location: prev.location || data.location,
        hero_images: scrapedImages.length > 0 ? scrapedImages : prev.hero_images,
        scraped_images: scrapedImages, // Track scraped images
        intro_message_en: data.personalized_message || prev.intro_message_en || data.meta_description,
        intro_message_de: data.personalized_message_de || prev.intro_message_de || data.meta_description,
        // Multi-language personalized messages
        detected_language: data.detected_language || prev.detected_language || "de",
        personalized_message_en: data.personalized_message_en || prev.personalized_message_en,
        personalized_message_de: data.personalized_message_de || prev.personalized_message_de,
        personalized_message_fr: data.personalized_message_fr || prev.personalized_message_fr,
        personalized_message_es: data.personalized_message_es || prev.personalized_message_es,
        personalized_message_it: data.personalized_message_it || prev.personalized_message_it,
        personalized_message_th: data.personalized_message_th || prev.personalized_message_th,
        logo_url: data.logo || prev.logo_url,
        about_text_en: data.about_text || prev.about_text_en,
        social_links: data.social_links || prev.social_links,
        video_url: data.video_url || prev.video_url,
        scraped_data: data
      }));
      
      const langName = { en: "English", de: "German", fr: "French", es: "Spanish", it: "Italian", th: "Thai" };
      const detectedLang = data.detected_language || "de";
      const videoFound = data.video_url ? " + 1 Video" : "";
      toast.success(`Fetched: ${scrapedImages.length} images${videoFound} • Language: ${langName[detectedLang]}`);
    } catch (error) {
      toast.error("Failed to scrape website");
    } finally {
      setScraping(false);
    }
  };

  const handleSave = async () => {
    if (!proposal.hotel_name) {
      toast.error("Hotel name is required");
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/proposals/${id}`, proposal, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Proposal updated");
      } else {
        const res = await axios.post(`${API}/proposals`, proposal, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Created: ${res.data.proposal_id}`);
        navigate(`/proposals/${res.data.id}/edit`);
      }
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const updatePlan = (planKey, field, value) => {
    setProposal(prev => ({
      ...prev,
      [planKey]: {
        ...prev[planKey],
        [field]: value
      }
    }));
  };

  const toggleFeatureInPlan = (planKey, feature) => {
    const currentFeatures = proposal[planKey]?.features || [];
    const hasFeature = currentFeatures.includes(feature);
    
    setProposal(prev => ({
      ...prev,
      [planKey]: {
        ...prev[planKey],
        features: hasFeature 
          ? currentFeatures.filter(f => f !== feature)
          : [...currentFeatures, feature]
      }
    }));
  };

  const addNewFeature = () => {
    if (!newFeature.trim()) return;
    if (allFeatures.includes(newFeature.trim())) {
      toast.error("Feature already exists");
      return;
    }
    // Add to all plans by default
    setProposal(prev => ({
      ...prev,
      plan_a: { ...prev.plan_a, features: [...(prev.plan_a?.features || []), newFeature.trim()] },
      plan_b: { ...prev.plan_b, features: [...(prev.plan_b?.features || []), newFeature.trim()] },
      plan_c: { ...prev.plan_c, features: [...(prev.plan_c?.features || []), newFeature.trim()] },
      plan_d: { ...prev.plan_d, features: [...(prev.plan_d?.features || []), newFeature.trim()] }
    }));
    setNewFeature("");
    toast.success("Feature added to all plans");
  };

  const removeFeatureFromAll = (feature) => {
    setProposal(prev => ({
      ...prev,
      plan_a: { ...prev.plan_a, features: (prev.plan_a?.features || []).filter(f => f !== feature) },
      plan_b: { ...prev.plan_b, features: (prev.plan_b?.features || []).filter(f => f !== feature) },
      plan_c: { ...prev.plan_c, features: (prev.plan_c?.features || []).filter(f => f !== feature) },
      plan_d: { ...prev.plan_d, features: (prev.plan_d?.features || []).filter(f => f !== feature) }
    }));
    toast.success("Feature removed");
  };

  const toggleAddOn = (addOnId) => {
    setProposal(prev => ({
      ...prev,
      add_on_ids: prev.add_on_ids.includes(addOnId)
        ? prev.add_on_ids.filter(id => id !== addOnId)
        : [...prev.add_on_ids, addOnId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  const tabs = [
    { id: "basic", label: "Basic Info" },
    { id: "pricing", label: "Pricing & Features" },
    { id: "addons", label: "Add-Ons" },
    { id: "settings", label: "Settings" }
  ];

  const planKeys = ['plan_a', 'plan_b', 'plan_c', 'plan_d'];
  const planLabels = ['Starter', 'Professional', 'Business', 'Enterprise'];

  return (
    <div className="p-6 lg:p-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-white" data-testid="proposal-editor-title">
            {isEditing ? "Edit Proposal" : "New Proposal"}
          </h1>
          {isEditing && proposal.proposal_id && (
            <p className="text-[#C9A96E] font-mono text-sm mt-0.5">{proposal.proposal_id}</p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Template Selector Button */}
          <div className="relative">
            <button
              onClick={() => setShowTemplateSelector(!showTemplateSelector)}
              className="btn-secondary text-sm px-4 py-2 flex items-center gap-2"
              data-testid="load-template-btn"
            >
              <LayoutTemplate className="w-4 h-4" />
              <span className="hidden sm:inline">Template</span>
            </button>
            
            {/* Template Dropdown */}
            {showTemplateSelector && templates.length > 0 && (
              <div className="absolute right-0 mt-2 w-72 glass-card-static rounded-xl shadow-xl z-50 py-2">
                <div className="px-4 py-2 border-b border-white/5">
                  <p className="text-white/60 text-xs uppercase tracking-wider">Load Template</p>
                </div>
                {templates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors flex items-center gap-3"
                  >
                    <Zap className="w-5 h-5 text-[#C9A96E]" />
                    <div>
                      <div className="text-white font-medium">{template.name}</div>
                      <div className="text-white/40 text-xs">
                        {template.sections?.foundation?.tiers
                          ? template.sections.foundation.tiers.map(t => t.name).join(" / ")
                          : `\u20AC${template.plan_a?.price || 0} / \u20AC${template.plan_b?.price || 0} / \u20AC${template.plan_c?.price || 0} / \u20AC${template.plan_d?.price || 0}`}
                      </div>
                    </div>
                    {template.is_default && (
                      <span className="ml-auto px-2 py-0.5 bg-[#C9A96E]/20 text-[#C9A96E] text-xs rounded-full">Default</span>
                    )}
                  </button>
                ))}
                <Link 
                  to="/templates"
                  className="block px-4 py-2 text-center text-[#C9A96E] text-sm hover:bg-white/5 border-t border-white/5"
                >
                  Manage Templates
                </Link>
              </div>
            )}
          </div>
          
          {isEditing && (
            <a
              href={`/p/${proposal.proposal_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-sm px-4 py-2"
              data-testid="preview-proposal-btn"
            >
              Preview
            </a>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-accent flex items-center gap-2 px-5 py-2.5"
            data-testid="save-proposal-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/5 mb-6 -mx-6 lg:-mx-8 px-6 lg:px-8">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "text-[#C9A96E] border-[#C9A96E]"
                  : "text-white/40 border-transparent hover:text-white"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div>
        {activeTab === "basic" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Website Scraper */}
            <div className="glass-card-static p-6">
              <h2 className="font-display text-lg text-white mb-4">Auto-Fill from Hotel Website</h2>
              <div className="flex gap-3">
                <input
                  type="url"
                  placeholder="https://hotel-website.com"
                  value={proposal.website_url}
                  onChange={(e) => setProposal(prev => ({ ...prev, website_url: e.target.value }))}
                  className="form-input flex-1"
                  data-testid="website-url-input"
                />
                <button
                  onClick={handleScrapeWebsite}
                  disabled={scraping}
                  className="btn-accent flex items-center gap-2 px-6"
                  data-testid="scrape-website-btn"
                >
                  {scraping ? <Loader2 className="w-5 h-5 animate-spin" /> : <Globe className="w-5 h-5" />}
                  Fetch Images & Info
                </button>
              </div>
            </div>

            {/* Scraped Images */}
            {proposal.hero_images && proposal.hero_images.length > 0 && (
              <div className="glass-card-static p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-lg text-white">Hotel Images ({proposal.hero_images.length})</h2>
                  <button
                    onClick={() => {
                      const url = prompt("Add image URL:");
                      if (url) {
                        setProposal(prev => ({
                          ...prev,
                          hero_images: [...(prev.hero_images || []), url]
                        }));
                      }
                    }}
                    className="btn-secondary text-sm px-3 py-1.5 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {proposal.hero_images.map((url, index) => {
                    const isScraped = proposal.scraped_images?.includes(url) || 
                                      proposal.scraped_data?.images?.includes(url);
                    const isVideo = url.match(/\.(mp4|webm|mov)$/i);
                    
                    return (
                      <div key={index} className="relative aspect-video rounded-xl overflow-hidden group bg-white/5">
                        {isVideo ? (
                          <video 
                            src={url} 
                            className="w-full h-full object-cover"
                            muted
                          />
                        ) : (
                          <img 
                            src={url} 
                            alt={`Hotel ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            onClick={() => setProposal(prev => ({
                              ...prev,
                              hero_images: prev.hero_images.filter((_, i) => i !== index)
                            }))}
                            className="p-2 bg-red-500/80 rounded-lg"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                        
                        {/* Badge indicators */}
                        <div className="absolute top-1 left-1 flex gap-1">
                          {index === 0 && (
                            <span className="px-2 py-0.5 bg-[#C9A96E] text-white text-[10px] rounded-full">
                              Hero
                            </span>
                          )}
                          {isVideo && (
                            <span className="px-2 py-0.5 bg-blue-500 text-white text-[10px] rounded-full flex items-center gap-1">
                              <Video className="w-3 h-3" /> Video
                            </span>
                          )}
                        </div>
                        
                        {/* Scraped indicator */}
                        {isScraped && (
                          <div className="absolute bottom-1 right-1 px-2 py-0.5 bg-emerald-500/90 text-white text-[10px] rounded-full flex items-center gap-1">
                            <Download className="w-3 h-3" /> Fetched
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Basic Info */}
            <div className="glass-card-static p-6">
              <h2 className="font-display text-lg text-white mb-4">Hotel Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group mb-0">
                  <label className="form-label">Hotel Name *</label>
                  <input
                    type="text"
                    value={proposal.hotel_name}
                    onChange={(e) => setProposal(prev => ({ ...prev, hotel_name: e.target.value }))}
                    className="form-input"
                    placeholder="Grand Luxury Resort"
                    data-testid="hotel-name-input"
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    value={proposal.location}
                    onChange={(e) => setProposal(prev => ({ ...prev, location: e.target.value }))}
                    className="form-input"
                    placeholder="Munich, Germany"
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="form-label">Contact Person</label>
                  <input
                    type="text"
                    value={proposal.contact_person}
                    onChange={(e) => setProposal(prev => ({ ...prev, contact_person: e.target.value }))}
                    className="form-input"
                    placeholder="John Doe"
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="form-label">Status</label>
                  <select
                    value={proposal.status}
                    onChange={(e) => setProposal(prev => ({ ...prev, status: e.target.value }))}
                    className="form-input"
                    data-testid="status-select"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "pricing" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            
            {/* 4 Plans Side by Side - Card View */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {planKeys.map((planKey, index) => {
                const plan = proposal[planKey] || defaultPlan;
                return (
                  <div 
                    key={planKey} 
                    className={`glass-card-static p-5 ${plan.is_popular ? 'ring-2 ring-[#C9A96E]' : ''}`}
                  >
                    {/* Popular Badge */}
                    {plan.is_popular && (
                      <div className="text-center mb-3">
                        <span className="px-3 py-1 bg-gradient-to-r from-[#C9A96E] to-[#C9A96E] text-white text-xs font-semibold rounded-full">
                          Most Popular
                        </span>
                      </div>
                    )}
                    
                    {/* Plan Name Input */}
                    <div className="mb-4">
                      <input
                        type="text"
                        value={plan.name}
                        onChange={(e) => updatePlan(planKey, "name", e.target.value)}
                        className="w-full bg-transparent border-none text-center font-display text-xl text-white focus:outline-none"
                        placeholder={planLabels[index]}
                      />
                    </div>
                    
                    {/* Price Input */}
                    <div className="text-center mb-4">
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-white/60">€</span>
                        <input
                          type="number"
                          value={plan.price}
                          onChange={(e) => updatePlan(planKey, "price", parseFloat(e.target.value) || 0)}
                          className="w-24 bg-transparent border-none text-center font-mono text-3xl text-white focus:outline-none"
                          placeholder="0"
                        />
                      </div>
                      <span className="text-white/40 text-sm">/month</span>
                    </div>
                    
                    {/* Popular Toggle */}
                    <button
                      onClick={() => updatePlan(planKey, "is_popular", !plan.is_popular)}
                      className={`w-full mb-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                        plan.is_popular 
                          ? "bg-[#C9A96E]/20 text-[#C9A96E]" 
                          : "bg-white/5 text-white/40 hover:bg-white/10"
                      }`}
                    >
                      <Star className={`w-4 h-4 ${plan.is_popular ? 'fill-current' : ''}`} />
                      {plan.is_popular ? 'Popular' : 'Set as Popular'}
                    </button>
                    
                    {/* Plan Description (shown as info tooltip on public page) */}
                    <div className="space-y-2 mt-2">
                      <input
                        type="text"
                        value={plan.description_en || ""}
                        onChange={(e) => updatePlan(planKey, "description_en", e.target.value)}
                        className="w-full bg-white/5 border-none rounded-lg px-3 py-1.5 text-white/60 text-xs focus:outline-none focus:ring-1 focus:ring-[#C9A96E]/30 placeholder-white/20"
                        placeholder="Plan description (EN) - shown on i-icon"
                      />
                      <input
                        type="text"
                        value={plan.info_video_url || ""}
                        onChange={(e) => updatePlan(planKey, "info_video_url", e.target.value)}
                        className="w-full bg-white/5 border-none rounded-lg px-3 py-1.5 text-white/60 text-xs focus:outline-none focus:ring-1 focus:ring-[#C9A96E]/30 placeholder-white/20"
                        placeholder="Video URL (optional)"
                      />
                    </div>
                    
                    {/* Feature Count */}
                    <div className="text-center text-white/40 text-sm">
                      {(plan.features || []).length} features included
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add New Feature */}
            <div className="glass-card-static p-6">
              <h3 className="font-display text-lg text-white mb-4">Add New Feature</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addNewFeature()}
                  className="form-input flex-1"
                  placeholder="Enter new feature name (e.g., 24/7 Support, Priority Listing...)"
                />
                <button
                  onClick={addNewFeature}
                  className="btn-accent px-6 flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Feature
                </button>
              </div>
              <p className="text-white/30 text-sm mt-2">
                New features are added to all plans. Toggle them on/off in the table below.
              </p>
            </div>

            {/* Feature Comparison Table */}
            <div className="glass-card-static overflow-hidden">
              <div className="p-5 lg:p-6 border-b border-white/5">
                <h3 className="font-display text-base text-white">Compare Plans</h3>
                <p className="text-white/30 text-xs mt-1">
                  Click toggles to include/exclude features per plan
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <div style={{ minWidth: '600px' }}>
                  {/* Grid Header */}
                  <div className="grid border-b border-white/10" style={{ gridTemplateColumns: '1fr repeat(4, 100px) 40px' }}>
                    <div className="py-3 px-4 text-white/30 text-[10px] uppercase tracking-widest font-medium">Feature</div>
                    {planKeys.map((planKey, i) => (
                      <div key={planKey} className="text-center py-3 px-1 border-l border-white/5">
                        <div className="text-white font-medium text-xs leading-tight">{proposal[planKey]?.name || planLabels[i]}</div>
                        <div className="text-[#C9A96E] font-mono text-xs mt-0.5">{"\u20AC"}{proposal[planKey]?.price || 0}</div>
                      </div>
                    ))}
                    <div className="p-1"></div>
                  </div>

                  {/* Grid Body */}
                  {allFeatures.length === 0 ? (
                    <div className="p-8 text-center text-white/30 text-sm">
                      No features added yet. Add your first feature above.
                    </div>
                  ) : (
                    allFeatures.map((feature, featureIndex) => (
                      <div
                        key={feature}
                        className={`grid items-center border-b border-white/5 group ${featureIndex % 2 !== 0 ? 'bg-white/[0.015]' : ''}`}
                        style={{ gridTemplateColumns: '1fr repeat(4, 100px) 40px' }}
                      >
                        <div className="py-2.5 px-4 text-white/80 text-sm truncate">{feature}</div>
                        {planKeys.map(planKey => {
                          const hasFeature = (proposal[planKey]?.features || []).includes(feature);
                          return (
                            <div key={planKey} className="flex justify-center py-2.5 px-1 border-l border-white/5">
                              <button
                                onClick={() => toggleFeatureInPlan(planKey, feature)}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                  hasFeature 
                                    ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25' 
                                    : 'bg-red-500/8 text-red-400/25 hover:bg-red-500/15 hover:text-red-400/60'
                                }`}
                              >
                                {hasFeature ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                              </button>
                            </div>
                          );
                        })}
                        <div className="flex justify-center py-2.5 px-1">
                          <button
                            onClick={() => removeFeatureFromAll(feature)}
                            className="p-1 opacity-0 group-hover:opacity-100 text-white/25 hover:text-red-400 hover:bg-red-500/10 rounded transition-all"
                            title="Remove feature"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Quick Feature Suggestions */}
            <div className="glass-card-static p-6">
              <h3 className="text-white/60 text-sm mb-3">Quick Add Common Features</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  "Basic Listing",
                  "Priority Listing", 
                  "Premium Placement",
                  "Email Support",
                  "Phone Support",
                  "24/7 Support",
                  "Dedicated Manager",
                  "Monthly Reports",
                  "Weekly Reports",
                  "Real-time Analytics",
                  "Social Media Boost",
                  "Marketing Campaigns",
                  "Custom Integrations",
                  "White-label Options",
                  "API Access"
                ].filter(f => !allFeatures.includes(f)).map(feature => (
                  <button
                    key={feature}
                    onClick={() => {
                      setNewFeature(feature);
                      setTimeout(addNewFeature, 0);
                    }}
                    className="px-3 py-1.5 bg-white/5 text-white/60 text-sm rounded-lg hover:bg-white/10 hover:text-white transition-colors"
                  >
                    + {feature}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "addons" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="glass-card-static p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-display text-lg text-white">Select Add-Ons</h2>
                  <p className="text-white/40 text-sm mt-1">Add-ons can be purchased separately with specific plans</p>
                </div>
                <Link
                  to="/addons"
                  className="text-[#C9A96E] text-sm hover:underline"
                >
                  Manage All Add-Ons →
                </Link>
              </div>

              {addOns.length === 0 ? (
                <div className="text-center py-12 text-white/40">
                  <p>No add-ons available. Create some first.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {addOns.map(addon => {
                    const isSelected = proposal.add_on_ids.includes(addon.id);
                    return (
                      <div
                        key={addon.id}
                        onClick={() => toggleAddOn(addon.id)}
                        className={`p-4 rounded-xl cursor-pointer transition-all ${
                          isSelected 
                            ? "bg-[#C9A96E]/10 border border-[#C9A96E]/40" 
                            : "bg-white/3 border border-white/5 hover:bg-white/5"
                        }`}
                        data-testid={`addon-${addon.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-white font-medium">{addon.name_en}</h3>
                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                            isSelected
                              ? "bg-[#C9A96E] border-[#C9A96E]"
                              : "border-white/20"
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                        <p className="text-white/40 text-sm mb-2">{addon.description_en}</p>
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[#C9A96E]">€{addon.price}</span>
                          <div className="flex gap-1">
                            {["A", "B", "C", "D"].map(p => (
                              <span 
                                key={p}
                                className={`w-5 h-5 rounded text-[10px] flex items-center justify-center ${
                                  addon.eligible_plans.includes(p)
                                    ? "bg-[#C9A96E]/20 text-[#C9A96E]"
                                    : "bg-white/5 text-white/20"
                                }`}
                              >
                                {p}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === "settings" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Offer Timing */}
            <div className="glass-card-static p-6">
              <h2 className="font-display text-lg text-white mb-4">Offer Timing & Discount</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="form-group mb-0">
                  <label className="form-label">Offer Start</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="form-input text-left w-full">
                        {proposal.offer_start 
                          ? format(new Date(proposal.offer_start), "PPP")
                          : "Select date"
                        }
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border-white/10">
                      <Calendar
                        mode="single"
                        selected={proposal.offer_start ? new Date(proposal.offer_start) : undefined}
                        onSelect={(date) => setProposal(prev => ({ 
                          ...prev, 
                          offer_start: date?.toISOString() || null 
                        }))}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="form-group mb-0">
                  <label className="form-label">Offer End</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="form-input text-left w-full">
                        {proposal.offer_end 
                          ? format(new Date(proposal.offer_end), "PPP")
                          : "Select date"
                        }
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0A0A0A] border-white/10">
                      <Calendar
                        mode="single"
                        selected={proposal.offer_end ? new Date(proposal.offer_end) : undefined}
                        onSelect={(date) => setProposal(prev => ({ 
                          ...prev, 
                          offer_end: date?.toISOString() || null 
                        }))}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="form-group mb-0">
                  <label className="form-label">Discount %</label>
                  <input
                    type="number"
                    value={proposal.discount_percent}
                    onChange={(e) => setProposal(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) || 0 }))}
                    className="form-input"
                    placeholder="0"
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="form-label">Discount Code</label>
                  <input
                    type="text"
                    value={proposal.discount_code}
                    onChange={(e) => setProposal(prev => ({ ...prev, discount_code: e.target.value }))}
                    className="form-input"
                    placeholder="WELCOME20"
                  />
                </div>
              </div>
            </div>

            {/* Deal Linking */}
            <div className="glass-card-static p-6">
              <h2 className="font-display text-lg text-white mb-4">Linked Deal</h2>
              <div className="form-group mb-0">
                <label className="form-label">Connect to Deal (optional)</label>
                <select
                  value={proposal.deal_id || ""}
                  onChange={(e) => setProposal(prev => ({ ...prev, deal_id: e.target.value || null }))}
                  className="form-input"
                  data-testid="proposal-deal-link"
                >
                  <option value="">No Deal Linked</option>
                  {deals.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.title}{d.company_name ? ` (${d.company_name})` : ""} — {d.status_label}
                    </option>
                  ))}
                </select>
                {proposal.deal_id && (
                  <p className="text-white/30 text-xs mt-2">This proposal will be linked to the selected deal bidirectionally.</p>
                )}
              </div>
            </div>

            {/* Access Control */}
            <div className="glass-card-static p-6">
              <h2 className="font-display text-lg text-white mb-4">Access Control</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group mb-0">
                  <label className="form-label">Access Type</label>
                  <select
                    value={proposal.access_type}
                    onChange={(e) => setProposal(prev => ({ ...prev, access_type: e.target.value }))}
                    className="form-input"
                  >
                    <option value="public">Public</option>
                    <option value="private">Private (Password)</option>
                  </select>
                </div>
                {proposal.access_type === "private" && (
                  <div className="form-group mb-0">
                    <label className="form-label">Page Password</label>
                    <input
                      type="text"
                      value={proposal.page_password}
                      onChange={(e) => setProposal(prev => ({ ...prev, page_password: e.target.value }))}
                      className="form-input"
                      placeholder="Enter password"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* CTA */}
            <div className="glass-card-static p-6">
              <h2 className="font-display text-lg text-white mb-4">Call-to-Action Button</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group mb-0">
                  <label className="form-label">CTA Text (English)</label>
                  <input
                    type="text"
                    value={proposal.cta_text_en}
                    onChange={(e) => setProposal(prev => ({ ...prev, cta_text_en: e.target.value }))}
                    className="form-input"
                    placeholder="Book Now"
                  />
                </div>
                <div className="form-group mb-0">
                  <label className="form-label">CTA Text (Deutsch)</label>
                  <input
                    type="text"
                    value={proposal.cta_text_de}
                    onChange={(e) => setProposal(prev => ({ ...prev, cta_text_de: e.target.value }))}
                    className="form-input"
                    placeholder="Jetzt Buchen"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
